import "./DatePickerPopover.scss";

import React from "react";
import { DatePickerWithSlotsMonth } from "./DatePickerMonth";
import { TrackedLink } from "../common/TrackedComponents";
import { FormattedMessage } from "react-intl";
import type { DeliveryDates, PickupDates } from "../../stores/DatesProvider";
import { DeliveryTimeSlots } from "../common/DeliveryTimeSlots";
import type { Timeslot } from "../../model/CheckoutData";
import { formatLocalDate, parseDate } from "../../utils/DateUtil";
import { addMonths, compareAsc, differenceInMonths, endOfMonth, getMonth, getYear, startOfMonth } from "date-fns";

/* Generic Date Picker Popover */
type DatePickerPopoverProps = {
	dateStrings: string[];
	onClose: () => void;
	renderMonth: (
		year: number,
		month: number,
		onPrevMonth?: (() => void) | null | undefined,
		onNextMonth?: (() => void) | null | undefined,
	) => React.ReactNode;
	renderBefore?: () => React.ReactNode;
	renderSlots?: () => React.ReactNode;
};
export const DatePickerPopover = ({
	dateStrings,
	onClose,
	renderMonth,
	renderBefore,
	renderSlots,
}: DatePickerPopoverProps) => {
	const monthsOfYears = getMonthsOfYears(dateStrings);
	const firstMonth = monthsOfYears[0];
	const secondMonth = monthsOfYears[1];
	const thirdMonth = monthsOfYears.length > 2 ? monthsOfYears[2] : null;
	return (
		<div className="mfcss_datepicker_calendar">
			<MobilePopoverHeader onClose={onClose} />
			{typeof renderBefore === "function" ? renderBefore() : null}
			<div className="mfcss_flexbox-post-container">
				{firstMonth ? (
					<div
						className={`mfcss_datepicker_month mfcss_flexbox-post-row${
							thirdMonth ? " has-third-month" : ""
						}`}
					>
						<div>{renderMonth(firstMonth.year, firstMonth.month)}</div>
						<div>{renderMonth(secondMonth.year, secondMonth.month)}</div>
						{thirdMonth ? <div>{renderMonth(thirdMonth.year, thirdMonth.month)}</div> : null}
					</div>
				) : null}
			</div>
			<div>{typeof renderSlots === "function" ? renderSlots() : null}</div>
		</div>
	);
};

function getMonthsOfYears(dates: string[]): {
	year: number;
	month: number;
}[] {
	if (!dates.length) return [];
	const sorted = [...dates].sort(compareAsc);
	const earliestDate = startOfMonth(parseDate(sorted[0]));
	const latestDate = endOfMonth(parseDate(sorted[sorted.length - 1]));
	const monthsDiff = differenceInMonths(latestDate, earliestDate);
	return [...Array(monthsDiff + 1).keys()]
		.map((offset) => addMonths(earliestDate, offset))
		.map((date) => ({
			year: getYear(date),
			month: getMonth(date),
		}));
}

export const MobilePopoverHeader = ({ onClose }: { onClose: () => void }) => (
	<div className="mfcss_datepicker_globalheader visible-xs">
		<div className="mfcss_datepicker_month">
			<div className="mfcss_datepicker_header">
				<div className="mfcss_datepicker_message">
					<FormattedMessage id="ORC_DELIVERY_DATE_CHOOSE" />
				</div>
				<TrackedLink
					tags={{
						source: "DateInputPicker.mobileClose",
					}}
					onClick={onClose}
					className="mfcss_datepicker_prev btn-square"
				>
					<i className="icon-close icon-lg" />
				</TrackedLink>
			</div>
		</div>
	</div>
);
export const DeliveryDateInfo = ({
	dates,
	selectedDate,
	dateFormat,
}: {
	dates: DeliveryDates;
	selectedDate: Date;
	dateFormat: string;
}) => {
	const dateInfo = dates.get(formatLocalDate(selectedDate, dateFormat));
	if (!dateInfo) return null;

	const getMessage = () => {
		if (dateInfo.type === "ValidDate") {
			if (dateInfo.isCustomerSlot && dateInfo.deliveryTimes.length) {
				return (
					<DeliveryTimeSlots date={selectedDate} timeSlots={dateInfo.deliveryTimes} iconClass="icon-clock" />
				);
			}

			if (dateInfo.isAlternative) {
				return <FormattedMessage id="ORC_DELIVERY_DATE_ALTERNATIVE_FOR_STORE_HOLIDAY_NOTIFICATION" />;
			}
		}

		return <FormattedMessage id="ORC_TIME_SLOT_DEFAULT_TEXT" />;
	};

	return <div className="mfcss_datepicker_timeslot_border_left mfcss_flexbox-post">{getMessage()}</div>;
};

/* Pickup Date Picker Popover */
export type PickupDatePickerPopoverProps = {
	dates: PickupDates;
	onClose: () => void;
	selectedDate: Date | null | undefined;
	onSelectDate: (date: Date, timeslot?: Timeslot | null | undefined) => void;
	dateFormat: string;
};
export const DatePickerWithSlotsPopover = ({
	dates,
	onClose,
	selectedDate,
	onSelectDate,
	dateFormat,
}: PickupDatePickerPopoverProps) => {
	const renderMonth = (
		year: number,
		month: number,
		onPrevMonth: (() => void) | null | undefined,
		onNextMonth: (() => void) | null | undefined,
	) => (
		<DatePickerWithSlotsMonth
			dates={dates}
			year={year}
			month={month}
			selectedDate={selectedDate}
			onSelectDate={onSelectDate}
			onPrevMonth={onPrevMonth}
			onNextMonth={onNextMonth}
			dateFormat={dateFormat}
		/>
	);

	return <DatePickerPopover dateStrings={[...dates.keys()]} onClose={onClose} renderMonth={renderMonth} />;
};
